import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Users, Dumbbell, TrendingUp } from 'lucide-react';

interface FrequencyBasedTemplateSelectionProps {
  onSelectTemplate: (frequency: number, templateType: 'fullBody' | 'upperLower') => void;
  onClose: () => void;
}

export default function FrequencyBasedTemplateSelection({ 
  onSelectTemplate, 
  onClose 
}: FrequencyBasedTemplateSelectionProps) {
  const [selectedFrequency, setSelectedFrequency] = useState<number | null>(null);

  const frequencyOptions = [
    {
      days: 2,
      title: "2 Days Per Week",
      subtitle: "Full Body Focus",
      templateType: 'fullBody' as const,
      icon: Users,
      benefits: [
        "Hit every muscle group twice per week",
        "Perfect for beginners or busy schedules", 
        "Maximum recovery between sessions",
        "Compound movements for efficiency"
      ],
      description: "Train all muscle groups in each session for optimal frequency with minimal time commitment."
    },
    {
      days: 3,
      title: "3-4 Days Per Week", 
      subtitle: "Upper/Lower Split",
      templateType: 'upperLower' as const,
      icon: Dumbbell,
      benefits: [
        "Higher training volume per muscle group",
        "Better focus on specific muscle groups",
        "Allows for more exercise variety",
        "Ideal progression from full body"
      ],
      description: "Alternate between upper and lower body days for increased volume and specialization."
    }
  ];

  const handleFrequencySelect = (option: typeof frequencyOptions[0]) => {
    setSelectedFrequency(option.days);
    // Small delay for visual feedback before proceeding
    setTimeout(() => {
      onSelectTemplate(option.days, option.templateType);
    }, 150);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold tracking-tight mb-2">How Often Do You Plan To Train?</h2>
        <p className="text-muted-foreground">
          We'll recommend the best workout structure based on your weekly frequency
        </p>
      </div>

      {/* Frequency Options */}
      <div className="grid gap-6">
        {frequencyOptions.map((option) => {
          const Icon = option.icon;
          const isSelected = selectedFrequency === option.days;
          
          return (
            <motion.div
              key={option.days}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <Card 
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isSelected 
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                    : 'hover:border-primary/50'
                }`}
                onClick={() => handleFrequencySelect(option)}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {option.title}
                          <Badge variant="secondary" className="text-xs">
                            {option.subtitle}
                          </Badge>
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {option.description}
                        </CardDescription>
                      </div>
                    </div>
                    
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-6 h-6 bg-primary rounded-full flex items-center justify-center"
                      >
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </motion.div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-foreground flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Why This Works:
                    </div>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      {option.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Skip Option */}
      <motion.div
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
      >
        <Button 
          variant="outline" 
          className="w-full h-auto p-4 justify-start border-dashed"
          onClick={onClose}
        >
          <Calendar className="w-4 h-4 mr-3" />
          <div className="text-left">
            <div className="font-medium">Skip Template Today</div>
            <div className="text-sm text-muted-foreground">
              Continue with flexible exercise logging
            </div>
          </div>
        </Button>
      </motion.div>
    </div>
  );
}